import React, { useState, useEffect, useMemo } from 'react';
import contentfulClient from '../../../src/contentfulClient';
import CustomButton from '../CustomButton/CustomButton'; 
const BLOG_POST_CONTENT_TYPE_ID = 'blogPost';

const POSTS_PER_LOAD = 3; // Define how many posts to load at a time
const INITIAL_POST_COUNT = POSTS_PER_LOAD; // The starting number of posts (3)

function BlogList() {
  // Store all published and filtered posts here
  const [allFilteredPosts, setAllFilteredPosts] = useState([]); 
  // Track how many posts are currently visible
  const [visiblePostCount, setVisiblePostCount] = useState(INITIAL_POST_COUNT); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to map Contentful item to a simplified post object
  const mapPostItem = (item) => {
    const fields = item.fields || {};
    const featuredImageFields = fields.featuredImage?.fields?.file; // Safely access nested fields
    
    if (!fields.title || !fields.slug || !fields.publishDate || !fields.excerpt) {
        console.warn("Skipping post due to missing required fields:", item.sys.id);
        return null;
    }

    // 🛑 FUTURE PUBLISH DATE CHECK 🛑
    const publishDate = new Date(fields.publishDate);
    const now = new Date();

    // If the publish date is in the future, return null to skip this post
    if (publishDate > now) {
        return null; 
    }
    
    const isFeaturedOnBrandVault = fields.featuredOnBrandVault || false;

    return {
      id: item.sys.id,
      title: fields.title,
      slug: fields.slug,
      excerpt: fields.excerpt,
      publishDate: fields.publishDate,
      featuredOnBrandVault: isFeaturedOnBrandVault,
      // FIX: Use the safely accessed fields
      imageUrl: featuredImageFields
        ? `https:${featuredImageFields.url}`
        : null,
    };
  };

  useEffect(() => {
    const fetchBlogPosts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await contentfulClient.getEntries({
          content_type: BLOG_POST_CONTENT_TYPE_ID,
          order: '-fields.publishDate',
        });

        const allPublishedPosts = response.items.map(mapPostItem).filter(post => post !== null);
        const featuredPosts = allPublishedPosts.filter(post => post.featuredOnBrandVault);

        // Store ALL filtered posts
        setAllFilteredPosts(featuredPosts);

      } catch (err) {
        console.error("Contentful Fetch Error on BlogList:", err);
        setError("Failed to load blog posts. Check Contentful connection and Content Model ID.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Slice the posts based on the current visiblePostCount
  const postsToShow = useMemo(() => {
    return allFilteredPosts.slice(0, visiblePostCount);
  }, [allFilteredPosts, visiblePostCount]);

  // Handler for the Load More button
  const handleLoadMore = () => {
    // Increase the count by POSTS_PER_LOAD (3)
    setVisiblePostCount(prevCount => prevCount + POSTS_PER_LOAD);
  };
  
  // Handler for the Load Less button
  const handleLoadLess = () => {
    setVisiblePostCount(prevCount => {
        // Ensure the count doesn't drop below the initial number of posts
        const newCount = prevCount - POSTS_PER_LOAD;
        return Math.max(newCount, INITIAL_POST_COUNT);
    });
  };

  // Check if there are more posts to load
  const hasMorePosts = allFilteredPosts.length > visiblePostCount;
  // Check if there are posts that can be removed (i.e., if we've loaded more than the initial 3)
  const canLoadLess = visiblePostCount > INITIAL_POST_COUNT;


  // --- JSX RENDER LOGIC ---
  if (isLoading) {
    return <div className="container mt-5 text-center">Loading Blog Posts...</div>;
  }

  if (error) {
    return <div className="container mt-5 text-danger text-center">Error: {error}</div>;
  }

  if (allFilteredPosts.length === 0) {
    return <div className="container mt-5 text-center">No published blog posts found.</div>;
  }

  return (
    <div id="blog-page" className="container my-5">
      {postsToShow.length > 0 && (
          <>
            <h2 className="text-black font-bold text-uppercase text-center fw-bold display-4 mb-5">Latest Blogs</h2>
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
              {/* Render only the posts in postsToShow */}
              {postsToShow.map((post) => (
                <div className="col" key={post.id}>
                  <a
                    href={`/blog/${post.slug}`}
                    className="text-decoration-none text-dark d-block h-100"
                  >
                    <div className="card h-100 shadow-sm transition-shadow hover-shadow-lg">
                      {post.imageUrl ? (
                        <img src={post.imageUrl} className="card-img-top" alt={post.title} style={{ height: '180px', objectFit: 'cover' }}/>
                      ) : (
                        <div style={{ height: '180px', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No Image</div>
                      )}
                      <div className="card-body">
                        <h5 className="card-title text-dark">{post.title}</h5>
                        <h6 className="card-subtitle mb-2 text-muted">
                          {new Date(post.publishDate).toLocaleDateString()}
                        </h6>
                        <p className="card-text text-secondary">{post.excerpt}</p>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </>
      )}

      {(hasMorePosts || canLoadLess) && (
        <div className="row justify-content-center mt-5 gx-3">
          {canLoadLess && (
            <div className="col-auto">
              <CustomButton
                label="LOAD LESS"
                variant="red" 
                size="small"
                onClick={handleLoadLess} 
                style={{ borderRadius: '3.125rem' }}
              />
            </div>
          )}

          {/* Load More Button (Visible if there are more posts to show) */}
          {hasMorePosts && (
            <div className="col-auto">
              <CustomButton
                label="LOAD MORE"
                variant="red"
                size="small"
                onClick={handleLoadMore} 
                style={{ borderRadius: '3.125rem' }}
              />
            </div>
          )}
        </div>
      )}
      {/* --- End Load Buttons Section --- */}
    </div>
  );
}

export default BlogList;